import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import staticFilesUrl from 'constants/staticFiles'
import {
  FullHeightWrapper,
  FullWidthWrapper as WidthWrapper,
} from 'components/sharedComponents/LayoutStyles'
import TabBar from 'components/sharedComponents/TabBar'
import {
  Button,
  TextHeadline1,
  TextHeadline2,
  TextBody as BuiTextBody,
  Link,
  colors,
} from '@blocket/blocket-ui'
import { withDevice, mediaQueries } from 'plugins/device'

const FullWidthWrapper = styled(WidthWrapper)`
  flex-grow: 1;
  background-color: ${colors.white100};
`

const InfoSection = styled.div`
  max-width: 400px;
  width: 100%;
  padding: 40px 16px 16px;

  @media ${mediaQueries.small} {
    padding: 72px 0px 16px;
  }
`

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 32px;
`

const TextSection = styled.div`
  text-align: left;
  white-space: pre-line;
`

const TextHeadlineWrapper = styled.div`
  margin-bottom: 16px;
`

const TextBody = styled(BuiTextBody)`
  margin-bottom: 40px;
`

const ReadMoreLink = styled(Link)`
  cursor: pointer;
  text-decoration: underline;
`

const DefaultIcon = styled.img`
  max-width: 160px;
`

const TextHeadline = ({ isMobile, text }) => (
  <TextHeadlineWrapper>
    {isMobile ? <TextHeadline2>{text}</TextHeadline2> : <TextHeadline1>{text}</TextHeadline1>}
  </TextHeadlineWrapper>
)

TextHeadline.Proptypes = {
  isMobile: PropTypes.bool,
  text: PropTypes.string,
}

const iconToUse = (type) => {
  switch (type) {
    case '400301_account_suspended':
    default:
      return <DefaultIcon alt="" src={`${staticFilesUrl}/assets/images/bankid-hand.svg`} />
  }
}

const defaultContent = {
  title: 'Kolla din mejl!',
  body:
    'För att säkerställa en trygg marknadsplats genomför vi en utökad granskning av användare vid behov.\n\nFör att verifiera att du är du - följ instruktionerna som skickats till mejlen som är kopplad till ditt Blocketkonto.\n\nMedan granskningen pågår kan du inte skicka eller ta emot meddelanden.',
  link: {
    url: 'https://blocket.zendesk.com/hc/sv/requests/new?ticket_form_id=360000445060',
    text: 'Läs mer om granskningen',
  },
  icon: iconToUse(),
  set: false,
}

const SuspendedAccount = ({ device }) => {
  const [content, setContent] = useState(defaultContent)
  const { userSuspendedType } = useSelector((state) => state.chat)
  const { suspendedReasons } = useSelector((state) => state.chat.clientSettings)
  const isMobile = !device.is('medium')

  useEffect(() => {
    if (!content.set && suspendedReasons && userSuspendedType) {
      const suspendReasonType = suspendedReasons.find((reason) => reason.type === userSuspendedType)
      return setContent({
        ...suspendReasonType,
        ...(suspendReasonType.icon && { icon: iconToUse(suspendReasonType.type) }),
        set: true,
      })
    }
  }, [content, suspendedReasons, userSuspendedType])

  return (
    <FullHeightWrapper>
      <TabBar />
      <FullWidthWrapper>
        <InfoSection>
          <IconWrapper>{content.icon}</IconWrapper>
          <TextSection>
            <TextHeadline isMobile={isMobile} text={content.title} />
            <TextBody>
              {content.body}{' '}
              {content.link?.url && content.link?.text && (
                <ReadMoreLink href={content.link.url} target="_blank" rel="noopener noreferrer">
                  {content.link.text}
                </ReadMoreLink>
              )}
            </TextBody>
          </TextSection>
          {content.cta?.url && content.cta?.text && (
            <Button type="primary" size="medium" href={content.cta.url}>
              {content.cta.text}
            </Button>
          )}
        </InfoSection>
      </FullWidthWrapper>
    </FullHeightWrapper>
  )
}

SuspendedAccount.Proptypes = {
  device: PropTypes.object.isRequired,
}

export default withDevice(SuspendedAccount)
